import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Table, Btn, Badge, Input, Select, Textarea, Modal, Card } from "@/components/UI";
import { Wrench, FileText, Users2, FlaskConical, AlertTriangle, Download, Plus, Trash2, Upload as UploadIcon, ChevronRight, Filter as FilterIcon, FileDown, RotateCcw, Eye, Printer, Pencil } from "lucide-react";
import { openPdfPreview } from "@/lib/pdfPreview";
import { previewPdf, downloadPdf, printPdf, resolvePdfEndpoint, filenameFor } from "@/lib/pdfActions";
import EquipmentInstructionCenter from "@/components/EquipmentInstructionCenter";
import ControlledContentCenter from "@/components/ControlledContentCenter";
import ControlledDocUploadModal from "@/components/ControlledDocUploadModal";
import MultiFileDropzone from "@/components/MultiFileDropzone";
import { todayTR } from "@/lib/date";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
const today = () => todayTR();
const levelBadge = (l) => l === "danger" ? <Badge level="missing">🔴 Gecikti</Badge> : l === "warn" ? <Badge level="attention">🟡 Yaklaşıyor</Badge> : <Badge level="ok">🟢 Uygun</Badge>;
const statusLabel = (s) => ({ compliant: "🟢 Uygun", non_compliant: "🔴 Uygun Değil", needs_action: "🟡 İşlem Gerek" }[s] || s);
const daysUntil = (iso) => { if (!iso) return null; const d = new Date(iso); const t = new Date(); return Math.floor((d - t) / 86400000); };
const dueLevel = (iso, warn = 30) => { const d = daysUntil(iso); if (d === null) return "info"; if (d < 0) return "danger"; if (d <= warn) return "warn"; return "ok"; };
const dueBadge = (iso, warn = 30) => { const d = daysUntil(iso); const l = dueLevel(iso, warn); if (!iso) return <span className="text-slate-400 text-xs">—</span>; return <span className={`text-xs px-2 py-0.5 rounded font-mono ${l === "danger" ? "bg-red-100 text-red-800" : l === "warn" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{iso} ({d < 0 ? `${-d} gün geçti` : `${d} gün`})</span>; };

/* ============= ORTAK FİLTRE BAR (Iter 113) ============= */
/**
 * Tarih kapsamı = "custom" | "daily" | "monthly" | "yearly" | "" (tümü).
 * scope + date-anchor gerçek start/end ISO değerlerini döner (backend-side).
 * `onPdf` çağrıldığında AKTİF filtre state'i ile PDF üretimi tetiklenir.
 */
export function GMPFilterBar({ scope, setScope, anchor, setAnchor, start, setStart, end, setEnd,
                                extraFilters, onFilter, onReset, onPdf, testidPrefix = "gmp" }) {
  return (
    <Card className="p-3 mb-3" data-testid={`${testidPrefix}-filter-bar`}>
      <div className="flex flex-wrap items-end gap-2 text-xs">
        <div className="text-[11px] font-bold text-slate-600 flex items-center gap-1"><FilterIcon size={12}/> Filtreler</div>
        <Select label="Tarih Kapsamı" value={scope} onChange={e => setScope(e.target.value)} data-testid={`${testidPrefix}-scope`}>
          <option value="">Tümü</option>
          <option value="daily">Günlük</option>
          <option value="monthly">Aylık</option>
          <option value="yearly">Yıllık</option>
          <option value="custom">İki Tarih Arası</option>
        </Select>
        {scope === "daily" && (
          <Input label="Gün" type="date" value={anchor || today()} onChange={e => setAnchor(e.target.value)} data-testid={`${testidPrefix}-anchor-day`}/>
        )}
        {scope === "monthly" && (
          <Input label="Ay" type="month" value={(anchor || today()).slice(0, 7)} onChange={e => setAnchor(e.target.value + "-01")} data-testid={`${testidPrefix}-anchor-month`}/>
        )}
        {scope === "yearly" && (
          <Input label="Yıl" type="number" value={(anchor || today()).slice(0, 4)} onChange={e => setAnchor(`${e.target.value}-01-01`)} min={2000} max={2100} data-testid={`${testidPrefix}-anchor-year`}/>
        )}
        {scope === "custom" && (<>
          <Input label="Başlangıç" type="date" value={start} onChange={e => setStart(e.target.value)} data-testid={`${testidPrefix}-start`}/>
          <Input label="Bitiş" type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid={`${testidPrefix}-end`}/>
        </>)}
        {extraFilters}
        <div className="flex gap-2 ml-auto">
          {onFilter && <Btn size="sm" variant="secondary" onClick={onFilter} data-testid={`${testidPrefix}-apply-filters`}><FilterIcon size={11}/> Filtrele</Btn>}
          <Btn size="sm" variant="ghost" onClick={onReset} data-testid={`${testidPrefix}-clear-filters`}><RotateCcw size={11}/> Filtreleri Temizle</Btn>
          <Btn size="sm" variant="primary" onClick={onPdf} data-testid={`${testidPrefix}-generate-pdf`} title="PDF Oluştur"><FileDown size={11}/></Btn>
        </div>
      </div>
    </Card>
  );
}

/** scope + anchor + start/end → {start, end} normalize (backend filter contract).
 * Backend her sayfada aynı `start`/`end` param'ını kabul eder. */
export function useScopedDateRange() {
  const [scope, setScope] = useState("");
  const [anchor, setAnchor] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const resolveRange = useCallback(() => {
    if (scope === "daily" && anchor) return { start: anchor, end: anchor };
    if (scope === "monthly" && anchor) {
      const [y, m] = anchor.split("-");
      const s = `${y}-${m}-01`;
      const nm = Number(m) === 12 ? `${Number(y)+1}-01-01` : `${y}-${String(Number(m)+1).padStart(2, "0")}-01`;
      // end inclusive → previous day of next month
      const d = new Date(nm); d.setDate(d.getDate() - 1);
      return { start: s, end: d.toISOString().slice(0, 10) };
    }
    if (scope === "yearly" && anchor) {
      const y = anchor.slice(0, 4);
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    }
    if (scope === "custom") return { start, end };
    return { start: "", end: "" };
  }, [scope, anchor, start, end]);
  const reset = () => { setScope(""); setAnchor(""); setStart(""); setEnd(""); };
  return { scope, setScope, anchor, setAnchor, start, setStart, end, setEnd, resolveRange, reset };
}

/** Download a PDF blob via the api with active filter params. */
export async function downloadFilteredPdf(pathBase, params, fallbackName) {
  const token = localStorage.getItem("erp_token");
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined)).toString();
  const url = `${API_BASE}${pathBase}${qs ? `?${qs}` : ""}`;
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { alert(`PDF alınamadı (HTTP ${res.status})`); return; }
    const disp = res.headers.get("content-disposition") || "";
    const m = disp.match(/filename="?([^";]+)"?/);
    const name = m ? m[1] : (fallbackName || "gmp_rapor.pdf");
    const blob = await res.blob();
    openPdfPreview(blob, name);
  } catch (e) {
    alert("PDF üretimi başarısız: " + (e?.message || "bilinmeyen hata"));
  }
}


async function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const b64 = r.result.split(",")[1]; resolve({ name: file.name, size: file.size, mime: file.type, data_base64: b64 }); };
    r.onerror = reject; r.readAsDataURL(file);
  });
}

/* ============= ALERTS BANNER ============= */
export function GMPAlertsBanner({ onGoto }) {
  const [data, setData] = useState({ alerts: [], counts: {} });
  useEffect(() => { api.get("/gmp/alerts").then(setData); const iv = setInterval(() => api.get("/gmp/alerts").then(setData), 60000); return () => clearInterval(iv); }, []);
  if (!data.alerts.length) return null;
  const top = data.alerts.slice(0, 5);
  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-red-50/40 p-3" data-testid="gmp-alerts-banner">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-bold text-amber-900"><AlertTriangle size={14}/> Yaklaşan İşlemler / GMP Uyarıları <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">{data.counts.danger || 0}🔴 · {data.counts.warn || 0}🟡</span></div>
      </div>
      <div className="space-y-1">
        {top.map((a, i) => (
          <button key={i} onClick={() => onGoto?.(a.tab, a.source_id)} className="w-full text-left text-xs px-2 py-1 rounded hover:bg-white/60 border border-transparent hover:border-amber-200 flex items-center justify-between gap-2" data-testid={`gmp-alert-${i}`}>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span>{a.level === "danger" ? "🔴" : "🟡"}</span>
              <span className="font-semibold truncate">{a.title}</span>
              <span className="text-slate-600 truncate">{a.message}</span>
            </div>
            <div className="flex items-center gap-1 text-slate-500 font-mono text-[10px]">{a.due_date} <ChevronRight size={11}/></div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ============= EQUIPMENT + MAINTENANCE TAB ============= */
export function MaintenanceTab({ highlightId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // iter309 — Normal GMP user: CREATE allowed; Admin/System Authority: UPDATE/DELETE allowed.
  const canCreate = !!user && (isAdmin || (user?.module_permissions?.gmp !== false));
  const canEditDelete = !!user && (isAdmin || !!user?.system_authority);
  const [equipment, setEquipment] = useState([]);
  const [records, setRecords] = useState([]);
  const [eqModal, setEqModal] = useState(null); // null or draft
  const [eqCenter, setEqCenter] = useState(null); // Iter210 Equipment Instruction Center
  const [eqDetail, setEqDetail] = useState(null); // iter309 — equipment history drawer
  const [rcModal, setRcModal] = useState(null);
  const [rcEditModal, setRcEditModal] = useState(null); // iter309 — edit existing record
  const dr = useScopedDateRange();
  const [filters, setFilters] = useState({ area: "", equipment_id: "", action_type: "", result: "", search: "", due_status: "" });
  const load = useCallback(async () => {
    const { start, end } = dr.resolveRange();
    const q = { ...filters, start, end };
    // remove empties
    Object.keys(q).forEach(k => { if (q[k] === "" || q[k] === undefined) delete q[k]; });
    const [e, r] = await Promise.all([
      api.get("/gmp/equipment", filters.area ? { area: filters.area } : {}),
      api.get("/gmp/maintenance-records", q),
    ]);
    setEquipment(e); setRecords(r);
    // iter309 — If detail drawer is open, re-sync the opened equipment doc + history.
    setEqDetail(prev => prev ? (e.find(x => x.id === prev.id) || prev) : prev);
  }, [filters, dr]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);
  const genPdf = () => {
    const { start, end } = dr.resolveRange();
    downloadFilteredPdf("/gmp/maintenance-records/pdf", { ...filters, start, end });
  };
  const resetAll = () => { dr.reset(); setFilters({ area: "", equipment_id: "", action_type: "", result: "", search: "", due_status: "" }); };
  const emptyEq = { name: "", area: "Laboratuvar", maintenance_required: true, calibration_required: false, last_maintenance: "", next_maintenance: "", last_calibration: "", next_calibration: "", compliance_status: "compliant", note: "" };
  const emptyRc = { equipment_id: "", action_type: "maintenance", performed_at: today(), next_due: "", result: "compliant", notes: "", files: [] };
  const saveEq = async () => {
    if (!eqModal.name) { alert("Ad zorunlu"); return; }
    try {
      if (eqModal.id) await api.put(`/gmp/equipment/${eqModal.id}`, eqModal);
      else await api.post("/gmp/equipment", eqModal);
      setEqModal(null); load();
    } catch (e) { alert(e.response?.data?.detail); }
  };
  const saveRc = async () => {
    if (!rcModal.equipment_id) { alert("Demirbaş seçiniz"); return; }
    try { await api.post("/gmp/maintenance-records", rcModal); setRcModal(null); load(); } catch (e) { alert(e.response?.data?.detail); }
  };
  // iter309 — Edit/Delete handlers for maintenance records (admin/system_authority only)
  const saveRcEdit = async () => {
    try {
      const { id, performed_at, next_due, result, notes, action_type } = rcEditModal;
      await api.put(`/gmp/maintenance-records/${id}`, { performed_at, next_due: next_due || null, result, notes, action_type });
      setRcEditModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Güncelleme başarısız"); }
  };
  const delRc = async (rid) => {
    if (!window.confirm("Bu bakım/kalibrasyon kaydı silinsin mi? İlgili demirbaşın tarihleri otomatik yeniden hesaplanır.")) return;
    try { await api.del(`/gmp/maintenance-records/${rid}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Silme başarısız"); }
  };
  const attachFiles = (newFiles) => {
    const arr = Array.isArray(newFiles) ? newFiles : [newFiles];
    if (arr.length === 0) return;
    setRcModal(p => ({ ...p, files: [...(p?.files || []), ...arr] }));
  };
  const removeAttach = (idx) => setRcModal(p => ({ ...p, files: (p?.files || []).filter((_, i) => i !== idx) }));
  const dlFile = (rid, idx, name) => {
    const token = localStorage.getItem("erp_token");
    fetch(`${API_BASE}/gmp/maintenance-records/${rid}/file/${idx}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); });
  };
  const delEq = async (id) => { if (window.confirm("Demirbaş silinsin/arşivlensin mi?")) { await api.del(`/gmp/equipment/${id}`); load(); } };
  // iter309 — Open detail drawer with pre-filled equipment selection for "İşlem Ekle"
  const openEqDetail = (eq) => setEqDetail(eq);
  const addRcFor = (eq) => setRcModal({ ...emptyRc, equipment_id: eq.id });
  const eqHistory = useMemo(() => {
    if (!eqDetail) return [];
    return records.filter(r => r.equipment_id === eqDetail.id);
  }, [eqDetail, records]);
  return (
    <div className="space-y-4">
      <GMPFilterBar
        {...dr}
        testidPrefix="maint"
        onReset={resetAll}
        onPdf={genPdf}
        extraFilters={<>
          <Select label="Ekipman" value={filters.equipment_id} onChange={e => setFilters({ ...filters, equipment_id: e.target.value })} data-testid="maint-equipment"><option value="">Tümü</option>{equipment.map(eq => <option key={eq.id} value={eq.id}>{eq.code} — {eq.name}</option>)}</Select>
          <Select label="İşlem Türü" value={filters.action_type} onChange={e => setFilters({ ...filters, action_type: e.target.value })} data-testid="maint-action-type"><option value="">Tümü</option><option value="maintenance">Bakım</option><option value="calibration">Kalibrasyon</option></Select>
          <Select label="Sonuç" value={filters.result} onChange={e => setFilters({ ...filters, result: e.target.value })} data-testid="maint-result"><option value="">Tümü</option><option value="compliant">Uygun</option><option value="non_compliant">Uygun Değil</option><option value="needs_action">İşlem Gerek</option></Select>
          <Select label="Sonraki Tarih" value={filters.due_status} onChange={e => setFilters({ ...filters, due_status: e.target.value })} data-testid="maint-due-status"><option value="">Tümü</option><option value="upcoming">Yaklaşanlar</option><option value="overdue">Gecikenler</option></Select>
          <Input label="Arama (Not)" value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="serbest metin" data-testid="maint-search"/>
        </>}
      />
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3"><div className="text-sm font-bold flex items-center gap-2"><Wrench size={14}/> Demirbaşlar</div>
          <div className="flex gap-2">
            {isAdmin && <Btn onClick={() => setEqModal({ ...emptyEq })} data-testid="add-equipment"><Plus size={12}/> Demirbaş Ekle</Btn>}
            {canCreate && <Btn variant="secondary" onClick={() => setRcModal({ ...emptyRc })} data-testid="add-maintenance"><Plus size={12}/> İşlem Ekle</Btn>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-3 text-xs">
          <Select label="Alan" value={filters.area} onChange={e => setFilters({ ...filters, area: e.target.value })}><option value="">Tümü</option>{["Laboratuvar","Üretim Alanı","Dolum Alanı","Depo","Ofis"].map(a => <option key={a} value={a}>{a}</option>)}</Select>
        </div>
        <Table testid="equipment-table" rows={equipment} columns={[
          { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
          { label: "Ad", render: r => (
            <button onClick={() => openEqDetail(r)} className="text-indigo-700 hover:text-indigo-900 hover:underline font-semibold text-left" data-testid={`eq-name-${r.id}`} title="İşlem geçmişini aç">
              {r.name}
            </button>
          ) },
          { label: "Alan", key: "area" },
          { label: "Son Bakım", render: r => r.last_maintenance || "—" },
          { label: "Sonraki Bakım", render: r => r.maintenance_required ? dueBadge(r.next_maintenance) : <span className="text-slate-300 text-xs">—</span> },
          { label: "Son Kalibrasyon", render: r => r.last_calibration || "—" },
          { label: "Sonraki Kalibrasyon", render: r => r.calibration_required ? dueBadge(r.next_calibration) : <span className="text-slate-300 text-xs">—</span> },
          { label: "Uygunluk", render: r => statusLabel(r.compliance_status) },
          { label: "İşlem", render: r => <div className="flex gap-1">
            {/* iter330 — İkon-only aksiyonlar + native title tooltip (1 sn hover). */}
            <Btn size="sm" variant="secondary" onClick={() => openEqDetail(r)} data-testid={`eq-detail-${r.id}`} title="Geçmiş"><Eye size={12}/></Btn>
            <Btn size="sm" variant="secondary" onClick={() => setEqCenter(r)} data-testid={`eq-center-${r.id}`} title="Talimat & Belgeler"><FileText size={12}/></Btn>
            {canEditDelete && <Btn size="sm" variant="secondary" onClick={() => setEqModal({ ...r })} data-testid={`edit-eq-${r.id}`} title="Düzenle"><Pencil size={12}/></Btn>}
            {canEditDelete && <Btn size="sm" variant="danger" onClick={() => delEq(r.id)} title="Sil"><Trash2 size={12}/></Btn>}
          </div> },
        ]}/>
      </Card>
      <Card className="p-4">
        <div className="text-sm font-bold mb-3">Bakım & Kalibrasyon Geçmişi</div>
        <Table testid="maintenance-records" rows={records} columns={[
          { label: "Tarih", render: r => r.performed_at },
          { label: "Demirbaş", render: r => r.equipment ? (
            <button onClick={() => { const eq = equipment.find(x => x.id === r.equipment_id); if (eq) openEqDetail(eq); }} className="text-indigo-700 hover:underline font-medium text-left" data-testid={`mr-eq-link-${r.id}`}>
              {r.equipment.code} — {r.equipment.name}
            </button>
          ) : r.equipment_id?.slice(0,8) },
          { label: "İşlem", render: r => r.action_type === "maintenance" ? "Bakım" : "Kalibrasyon" },
          { label: "Sonraki", render: r => r.next_due || "—" },
          { label: "Sonuç", render: r => statusLabel(r.result) },
          { label: "Belge", render: r => (r.files || []).length ? (r.files || []).map((f, i) => <button key={i} onClick={() => dlFile(r.id, i, f.name)} className="text-xs text-indigo-600 hover:underline mr-1" data-testid={`dl-file-${r.id}-${i}`}><Download size={10} className="inline"/> {f.name}</button>) : <span className="text-slate-300">—</span> },
          { label: "Kullanıcı", render: r => r.created_by_name },
          { label: "İşlem", render: r => canEditDelete ? (
            <div className="flex gap-1" data-testid={`mr-actions-${r.id}`}>
              <Btn size="sm" variant="secondary" onClick={() => setRcEditModal({ ...r })} data-testid={`mr-edit-${r.id}`} title="Düzenle"><Pencil size={12}/></Btn>
              <Btn size="sm" variant="danger" onClick={() => delRc(r.id)} data-testid={`mr-del-${r.id}`} title="Sil"><Trash2 size={12}/></Btn>
            </div>
          ) : <span className="text-slate-300 text-xs">—</span> },
        ]}/>
      </Card>

      <Modal open={!!eqModal} onClose={() => setEqModal(null)} title={eqModal?.id ? "Demirbaş Düzenle" : "Yeni Demirbaş"} size="lg">
        {eqModal && <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Input label="Demirbaş Adı *" value={eqModal.name} onChange={e => setEqModal({ ...eqModal, name: e.target.value })} data-testid="eq-name"/></div>
          <Select label="Alan *" value={eqModal.area} onChange={e => setEqModal({ ...eqModal, area: e.target.value })}>{["Laboratuvar","Üretim Alanı","Dolum Alanı","Depo","Ofis"].map(a => <option key={a} value={a}>{a}</option>)}</Select>
          <Select label="Uygunluk" value={eqModal.compliance_status} onChange={e => setEqModal({ ...eqModal, compliance_status: e.target.value })}><option value="compliant">Uygun</option><option value="non_compliant">Uygun Değil</option><option value="needs_action">İşlem Gerek</option></Select>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={eqModal.maintenance_required} onChange={e => setEqModal({ ...eqModal, maintenance_required: e.target.checked })}/> Bakım Gerekiyor</label>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={eqModal.calibration_required} onChange={e => setEqModal({ ...eqModal, calibration_required: e.target.checked })}/> Kalibrasyon Gerekiyor</label>
          <Input label="Son Bakım" type="date" value={eqModal.last_maintenance || ""} onChange={e => setEqModal({ ...eqModal, last_maintenance: e.target.value })}/>
          <Input label="Sonraki Bakım" type="date" value={eqModal.next_maintenance || ""} onChange={e => setEqModal({ ...eqModal, next_maintenance: e.target.value })}/>
          <Input label="Son Kalibrasyon" type="date" value={eqModal.last_calibration || ""} onChange={e => setEqModal({ ...eqModal, last_calibration: e.target.value })}/>
          <Input label="Sonraki Kalibrasyon" type="date" value={eqModal.next_calibration || ""} onChange={e => setEqModal({ ...eqModal, next_calibration: e.target.value })}/>
          <div className="col-span-2"><Textarea label="Not" value={eqModal.note || ""} onChange={e => setEqModal({ ...eqModal, note: e.target.value })}/></div>
        </div>}
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setEqModal(null)}>Vazgeç</Btn><Btn onClick={saveEq} data-testid="save-equipment">Kaydet</Btn></div>
      </Modal>

      <Modal open={!!rcModal} onClose={() => setRcModal(null)} title="Bakım / Kalibrasyon İşlemi Ekle" size="lg">
        {rcModal && <div className="grid grid-cols-2 gap-3">
          <Select label="Demirbaş *" value={rcModal.equipment_id} onChange={e => setRcModal({ ...rcModal, equipment_id: e.target.value })} data-testid="rc-equipment"><option value="">Seçiniz...</option>{equipment.map(eq => <option key={eq.id} value={eq.id}>{eq.code} — {eq.name}</option>)}</Select>
          <Select label="İşlem Türü *" value={rcModal.action_type} onChange={e => setRcModal({ ...rcModal, action_type: e.target.value })} data-testid="rc-type"><option value="maintenance">Bakım</option><option value="calibration">Kalibrasyon</option></Select>
          <Input label="İşlem Tarihi *" type="date" value={rcModal.performed_at} onChange={e => setRcModal({ ...rcModal, performed_at: e.target.value })} data-testid="rc-date"/>
          <Input label="Sonraki İşlem" type="date" value={rcModal.next_due || ""} onChange={e => setRcModal({ ...rcModal, next_due: e.target.value })} data-testid="rc-next"/>
          <Select label="Sonuç" value={rcModal.result} onChange={e => setRcModal({ ...rcModal, result: e.target.value })} data-testid="rc-result"><option value="compliant">Uygun</option><option value="non_compliant">Uygun Değil</option><option value="needs_action">İşlem Gerek</option></Select>
          <div className="col-span-2"><Textarea label="Açıklama" value={rcModal.notes || ""} onChange={e => setRcModal({ ...rcModal, notes: e.target.value })}/></div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-600 block mb-1">Belge / Sertifika / Fatura</label>
            <MultiFileDropzone
              files={rcModal.files || []}
              onFilesAdded={attachFiles}
              onRemove={removeAttach}
              label="Bakım/kalibrasyon belgelerini sürükle-bırak veya tıklayarak seç"
              testid="rc-file"
            />
          </div>
        </div>}
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setRcModal(null)}>Vazgeç</Btn><Btn onClick={saveRc} data-testid="save-maintenance">Kaydet</Btn></div>
      </Modal>

      {/* iter309 — Edit existing maintenance record (admin/system_authority only) */}
      <Modal open={!!rcEditModal} onClose={() => setRcEditModal(null)} title="Bakım / Kalibrasyon Kaydı Düzenle" size="lg">
        {rcEditModal && <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 p-2 bg-amber-50 border border-amber-300 rounded text-[11px] text-amber-900">
            <b>Not:</b> Tarih değiştiğinde ilgili demirbaşın <b>Son Bakım/Kalibrasyon</b> ve <b>Sonraki Bakım/Kalibrasyon</b> alanları otomatik yeniden hesaplanır.
          </div>
          <Input label="Demirbaş" value={rcEditModal.equipment ? `${rcEditModal.equipment.code} — ${rcEditModal.equipment.name}` : rcEditModal.equipment_id} disabled/>
          <Select label="İşlem Türü *" value={rcEditModal.action_type} onChange={e => setRcEditModal({ ...rcEditModal, action_type: e.target.value })} data-testid="rc-edit-type"><option value="maintenance">Bakım</option><option value="calibration">Kalibrasyon</option></Select>
          <Input label="İşlem Tarihi *" type="date" value={rcEditModal.performed_at || ""} onChange={e => setRcEditModal({ ...rcEditModal, performed_at: e.target.value })} data-testid="rc-edit-date"/>
          <Input label="Sonraki İşlem" type="date" value={rcEditModal.next_due || ""} onChange={e => setRcEditModal({ ...rcEditModal, next_due: e.target.value })} data-testid="rc-edit-next"/>
          <Select label="Sonuç" value={rcEditModal.result || "compliant"} onChange={e => setRcEditModal({ ...rcEditModal, result: e.target.value })} data-testid="rc-edit-result"><option value="compliant">Uygun</option><option value="non_compliant">Uygun Değil</option><option value="needs_action">İşlem Gerek</option></Select>
          <div className="col-span-2"><Textarea label="Açıklama" value={rcEditModal.notes || ""} onChange={e => setRcEditModal({ ...rcEditModal, notes: e.target.value })} data-testid="rc-edit-notes"/></div>
        </div>}
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setRcEditModal(null)}>Vazgeç</Btn><Btn onClick={saveRcEdit} data-testid="save-maintenance-edit">Güncelle</Btn></div>
      </Modal>

      {/* iter309 — Equipment detail drawer with per-equipment history */}
      <Modal open={!!eqDetail} onClose={() => setEqDetail(null)} title={eqDetail ? `${eqDetail.code} — ${eqDetail.name}` : ""} size="full">
        {eqDetail && (
          <div className="space-y-4" data-testid="eq-detail-drawer">
            <Card className="p-4 bg-slate-50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><div className="text-slate-500">Demirbaş Kodu</div><div className="font-mono font-bold">{eqDetail.code}</div></div>
                <div><div className="text-slate-500">Alan / Konum</div><div className="font-semibold">{eqDetail.area}</div></div>
                <div><div className="text-slate-500">Uygunluk</div><div>{statusLabel(eqDetail.compliance_status)}</div></div>
                <div><div className="text-slate-500">Not</div><div className="text-slate-700">{eqDetail.note || "—"}</div></div>
                <div><div className="text-slate-500">Son Bakım</div><div className="font-semibold" data-testid="eq-detail-last-maint">{eqDetail.last_maintenance || "—"}</div></div>
                <div><div className="text-slate-500">Sonraki Bakım</div><div data-testid="eq-detail-next-maint">{eqDetail.maintenance_required ? dueBadge(eqDetail.next_maintenance) : <span className="text-slate-300">—</span>}</div></div>
                <div><div className="text-slate-500">Son Kalibrasyon</div><div className="font-semibold" data-testid="eq-detail-last-calib">{eqDetail.last_calibration || "—"}</div></div>
                <div><div className="text-slate-500">Sonraki Kalibrasyon</div><div data-testid="eq-detail-next-calib">{eqDetail.calibration_required ? dueBadge(eqDetail.next_calibration) : <span className="text-slate-300">—</span>}</div></div>
              </div>
              <div className="flex gap-2 mt-3">
                {canCreate && <Btn size="sm" onClick={() => addRcFor(eqDetail)} data-testid="eq-detail-add-rc"><Plus size={12}/> Yeni İşlem Ekle</Btn>}
                <Btn size="sm" variant="secondary" onClick={() => setEqCenter(eqDetail)} data-testid="eq-detail-instructions"><FileText size={12}/> Kullanım Talimatı</Btn>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-bold flex items-center gap-2"><Wrench size={14}/> İşlem Geçmişi</div>
                <div className="text-xs text-slate-500" data-testid="eq-detail-history-count">Toplam {eqHistory.length} işlem</div>
              </div>
              {eqHistory.length === 0 ? (
                <div className="text-sm text-slate-400 italic py-6 text-center">Bu demirbaş için henüz işlem kaydı yok.</div>
              ) : (
                <div className="max-h-[500px] overflow-auto">
                  <Table testid="eq-detail-history" rows={eqHistory} columns={[
                    { label: "Tarih", render: r => <span className="font-mono">{r.performed_at}</span> },
                    { label: "Tür", render: r => r.action_type === "maintenance" ? <Badge level="attention">Bakım</Badge> : <Badge level="info">Kalibrasyon</Badge> },
                    { label: "Sonraki", render: r => r.next_due ? <span className="font-mono text-xs">{r.next_due}</span> : <span className="text-slate-300">—</span> },
                    { label: "Sonuç", render: r => statusLabel(r.result) },
                    { label: "Açıklama", render: r => <span className="text-xs">{r.notes || "—"}</span> },
                    { label: "Belge", render: r => (r.files || []).length ? (r.files || []).map((f, i) => <button key={i} onClick={() => dlFile(r.id, i, f.name)} className="text-xs text-indigo-600 hover:underline mr-1"><Download size={10} className="inline"/> {f.name}</button>) : <span className="text-slate-300">—</span> },
                    { label: "Kayıt", render: r => <div className="text-[11px] text-slate-600">{r.created_by_name}<br/><span className="text-slate-400">{(r.created_at || "").slice(0,10)}</span></div> },
                    { label: "İşlem", render: r => canEditDelete ? (
                      <div className="flex gap-1" data-testid={`eq-detail-actions-${r.id}`}>
                        <Btn size="sm" variant="secondary" onClick={() => setRcEditModal({ ...r })} data-testid={`eq-detail-edit-${r.id}`} title="Düzenle"><Pencil size={12}/></Btn>
                        <Btn size="sm" variant="danger" onClick={() => delRc(r.id)} data-testid={`eq-detail-del-${r.id}`} title="Sil"><Trash2 size={12}/></Btn>
                      </div>
                    ) : <span className="text-slate-300 text-xs">Görüntüle</span> },
                  ]}/>
                </div>
              )}
            </Card>
          </div>
        )}
      </Modal>

      {eqCenter && (
        <EquipmentInstructionCenter equipment={eqCenter} open={!!eqCenter}
          onClose={() => setEqCenter(null)}/>
      )}
    </div>
  );
}

/* ============= DOCUMENTS / SOP TAB — Iter240 canonical library ============= */
// Doküman & SOP is now a READ-ONLY canonical library view of the same
// master_documents inventory that Master Doküman Listesi manages. All CRUD
// (Yeni Doküman / Yeni Revizyon / Silme) happens ONLY in Master Doküman
// Listesi. Legacy /api/gmp/documents evidence records are NOT surfaced
// here — they stay untouched for archival.
export function DocumentsTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ q: "", document_type: "", module: "", status: "" });
  const [uploadOpen, setUploadOpen] = useState(false);  // iter322

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const p = {};
      if (f.q) p.q = f.q;
      if (f.document_type) p.document_type = f.document_type;
      if (f.module) p.module = f.module;
      let out = [];
      try {
        out = (await api.get("/gmp/master-documents/inventory", p)) || [];
      } catch {
        out = (await api.get("/gmp/master-documents", p)) || [];
      }
      if (f.status) out = out.filter(r => r.status === f.status);
      setRows(out);
    } finally { setBusy(false); }
  }, [f]);
  useEffect(() => { load(); }, [f.document_type, f.module, f.status]);

  // The canonical PDF Preview / Download / Print action group — reuses the
  // same shared /lib/pdfActions helpers so Master Doküman Listesi and
  // Doküman & SOP call the identical backend endpoint.
  const CanonicalPdfActions = ({ row }) => {
    const isL01 = row.base_code === "L01/KYB"
      || (row.current_full_code || "").startsWith("L01/KYB");
    const ep = isL01
      ? { kind: "live", path: "/api/gmp/reports/L01/pdf" }
      : resolvePdfEndpoint(row);

    // "Kaynak modüle git" — record-based / OPERATIONAL_PDF_ONLY documents
    // don't have a direct blank/list generator here; user opens them from
    // the source module. This message is aligned with Master Doküman Listesi.
    if (!ep) {
      if (row.source_route) {
        return (
          <button
            type="button"
            title={`Kaynak: ${row.source_page || row.source_route}`}
            onClick={() => { window.location.hash = row.source_route; }}
            data-testid={`sop-goto-src-${row.id}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 border">
            <ChevronRight size={12}/> Kaynak modüle git
          </button>
        );
      }
      return <span className="text-slate-300 text-xs">—</span>;
    }
    const fname = filenameFor(row, ep.kind === "blank" ? "Bos_Sablon" : "");
    return (
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => previewPdf(ep.path, fname)}
          title="Önizle"
          data-testid={`sop-preview-${row.id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded bg-indigo-600 hover:bg-indigo-700 text-white">
          <Eye size={14}/>
        </button>
        <button
          type="button"
          onClick={() => downloadPdf(ep.path, fname)}
          title="İndir"
          data-testid={`sop-download-${row.id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded bg-slate-100 hover:bg-slate-200 text-slate-800 border">
          <Download size={14}/>
        </button>
        <button
          type="button"
          onClick={() => printPdf(ep.path, fname)}
          title="Yazdır"
          data-testid={`sop-print-${row.id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded bg-slate-100 hover:bg-slate-200 text-slate-800 border">
          <Printer size={14}/>
        </button>
      </div>
    );
  };

  const filtered = useMemo(() => {
    const q = f.q?.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (
      (r.base_code || "") + " " + (r.current_full_code || "") + " " +
      (r.title || "") + " " + (r.source_module || "")
    ).toLowerCase().includes(q));
  }, [rows, f.q]);

  const typeLabel = (t) => ({
    FORM: "Form", LIST: "Liste", REPORT: "Rapor", INSTRUCTION: "Talimat",
    SOP: "SOP", CONTROLLED: "Kontrollü",
  }[t] || t || "—");
  const statusLevel = (s) => (s === "ACTIVE" ? "ok" : s === "OBSOLETE" ? "inactive" : "attention");
  const statusLabel = (s) => ({
    DRAFT: "Taslak", IN_REVIEW: "İncelemede", ACTIVE: "Yürürlükte",
    OBSOLETE: "İptal", ARCHIVED: "Arşiv",
  }[s] || s || "—");

  return (
    <div>
      <Card className="p-3 mb-3" data-testid="sop-library-info">
        <div className="flex items-start gap-3">
          <div className="text-xs text-slate-600 leading-relaxed flex-1">
            <span className="font-semibold text-indigo-700">Doküman & SOP Kütüphanesi</span> —
            Master Doküman Listesi ile aynı kanonik kaynağı okur. Arama, filtreleme, Önizle / İndir / Yazdır işlemleri için kullanın.
            Yeni doküman, revizyon veya durum değişikliği <span className="font-semibold">Master Doküman Listesi</span> sekmesinden yapılır.
          </div>
          <Btn size="sm" onClick={() => setUploadOpen(true)} data-testid="sop-upload-new">
            <UploadIcon size={12}/> + Belge Ekle
          </Btn>
        </div>
      </Card>

      <ControlledDocUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onCreated={load}/>

      <Card className="p-3 mb-3" data-testid="sop-filter-bar">
        <div className="flex flex-wrap items-end gap-2">
          <Input
            label="Arama"
            value={f.q}
            onChange={e => setF({ ...f, q: e.target.value })}
            placeholder="kod / başlık / modül"
            data-testid="sop-search"/>
          <Select label="Tür" value={f.document_type}
            onChange={e => setF({ ...f, document_type: e.target.value })}
            data-testid="sop-type-filter">
            <option value="">Tümü</option>
            <option value="FORM">Form</option>
            <option value="LIST">Liste</option>
            <option value="REPORT">Rapor</option>
            <option value="INSTRUCTION">Talimat</option>
            <option value="SOP">SOP</option>
            <option value="CONTROLLED">Kontrollü</option>
          </Select>
          <Select label="Durum" value={f.status}
            onChange={e => setF({ ...f, status: e.target.value })}
            data-testid="sop-status-filter">
            <option value="">Tümü</option>
            <option value="ACTIVE">Yürürlükte</option>
            <option value="DRAFT">Taslak</option>
            <option value="IN_REVIEW">İncelemede</option>
            <option value="OBSOLETE">İptal</option>
            <option value="ARCHIVED">Arşiv</option>
          </Select>
          <Input
            label="Kaynak Modül"
            value={f.module}
            onChange={e => setF({ ...f, module: e.target.value })}
            placeholder="rd / gmp / production…"
            data-testid="sop-module-filter"/>
          <div className="flex-1"/>
          <div className="text-xs text-slate-500 pt-4" data-testid="sop-count">
            {filtered.length} doküman
          </div>
        </div>
      </Card>

      <Table testid="sop-library-table" rows={filtered} columns={[
        { label: "Kod", render: r => (
          <span className="font-mono text-xs font-semibold text-indigo-700"
                data-testid={`sop-code-${r.id}`}>
            {r.current_full_code || r.base_code || "—"}
          </span>
        )},
        { label: "Başlık", render: r => (
          <span className="text-sm" data-testid={`sop-title-${r.id}`}>{r.title || "—"}</span>
        )},
        { label: "Tür", render: r => (
          <span className="text-xs px-2 py-0.5 rounded bg-slate-100 border">
            {typeLabel(r.document_type)}
          </span>
        )},
        { label: "Rev.", render: r => (
          <span className="font-mono text-xs bg-slate-50 px-2 py-0.5 rounded border">
            {typeof r.current_revision_no === "number"
              ? String(r.current_revision_no).padStart(2, "0")
              : "—"}
          </span>
        )},
        { label: "Durum", render: r => (
          <Badge level={statusLevel(r.status)}>{statusLabel(r.status)}</Badge>
        )},
        { label: "Kaynak Modül", render: r => (
          <span className="text-xs text-slate-600">
            {r.source_module || "—"}
            {r.source_page ? <span className="text-slate-400"> · {r.source_page}</span> : null}
          </span>
        )},
        { label: "İşlem", render: r => <CanonicalPdfActions row={r}/> },
      ]}/>
      {!busy && filtered.length === 0 && (
        <div className="text-center py-6 text-slate-400 text-sm">Filtreye uygun doküman yok.</div>
      )}
    </div>
  );
}

/* ============= SUPPLIER EVALUATIONS TAB ============= */
export function SupplierEvalTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [modal, setModal] = useState(null);
  const dr = useScopedDateRange();
  const [filters, setFilters] = useState({ supplier_id: "", status: "", score_min: "", score_max: "", search: "" });
  const load = useCallback(() => {
    const { start, end } = dr.resolveRange();
    const q = { ...filters, start, end };
    Object.keys(q).forEach(k => { if (q[k] === "" || q[k] === undefined) delete q[k]; });
    return Promise.all([api.get("/gmp/supplier-evaluations", q), api.get("/suppliers")]).then(([r, s]) => { setRows(r); setSuppliers(s); });
  }, [filters, dr]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);
  const genPdf = () => {
    const { start, end } = dr.resolveRange();
    downloadFilteredPdf("/gmp/supplier-evaluations/pdf", { ...filters, start, end });
  };
  const resetAll = () => { dr.reset(); setFilters({ supplier_id: "", status: "", score_min: "", score_max: "", search: "" }); };
  const criteria = [
    { key: "quality", label: "Ürün / Malzeme Kalitesi" },
    { key: "delivery", label: "Teslimat Performansı" },
    { key: "documents", label: "Belge Uygunluğu" },
    { key: "communication", label: "İletişim" },
    { key: "history", label: "Uygunsuzluk Geçmişi (yüksek=iyi)" },
    { key: "overall", label: "Genel Değerlendirme" },
  ];
  const empty = { supplier_id: "", scores: Object.fromEntries(criteria.map(c => [c.key, 3])), result_notes: "", next_evaluation_date: "" };
  const save = async () => {
    if (!modal.supplier_id) { alert("Tedarikçi seçiniz"); return; }
    try { await api.post("/gmp/supplier-evaluations", modal); setModal(null); load(); } catch (e) { alert(e.response?.data?.detail); }
  };
  const del = async (id) => { if (window.confirm("Silinsin mi?")) { await api.del(`/gmp/supplier-evaluations/${id}`); load(); } };
  const supStatusBadge = (s) => s === "approved" ? <Badge level="ok">🟢 Onaylı</Badge> : s === "conditional" ? <Badge level="attention">🟡 Koşullu</Badge> : <Badge level="missing">🔴 Uygun Değil</Badge>;
  return (
    <div>
      <GMPFilterBar
        {...dr}
        testidPrefix="supp"
        onReset={resetAll}
        onPdf={genPdf}
        extraFilters={<>
          <Select label="Tedarikçi" value={filters.supplier_id} onChange={e => setFilters({ ...filters, supplier_id: e.target.value })} data-testid="supp-supplier-filter"><option value="">Tümü</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
          <Select label="Sonuç" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} data-testid="supp-status-filter"><option value="">Tümü</option><option value="approved">Onaylı</option><option value="conditional">Koşullu</option><option value="rejected">Uygun Değil</option></Select>
          <Input label="Puan Min" type="number" step="0.1" min="1" max="5" value={filters.score_min} onChange={e => setFilters({ ...filters, score_min: e.target.value })} data-testid="supp-score-min"/>
          <Input label="Puan Max" type="number" step="0.1" min="1" max="5" value={filters.score_max} onChange={e => setFilters({ ...filters, score_max: e.target.value })} data-testid="supp-score-max"/>
          <Input label="Arama (Not)" value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="serbest metin" data-testid="supp-search"/>
        </>}
      />
      {isAdmin && <div className="flex justify-end mb-3"><Btn onClick={() => setModal({ ...empty })} data-testid="add-supplier-eval"><Plus size={12}/> Yeni Değerlendirme</Btn></div>}
      <Table testid="supplier-evals" rows={rows} columns={[
        { label: "Tarih", render: r => (r.evaluated_at || "").slice(0, 10) },
        { label: "Tedarikçi", render: r => r.supplier_name },
        { label: "Değerlendiren", render: r => r.evaluated_by_name },
        { label: "Puan", render: r => `${r.average}/5` },
        { label: "%", render: r => `%${r.percentage}` },
        { label: "Sonuç", render: r => supStatusBadge(r.status) },
        { label: "Sonraki", render: r => dueBadge(r.next_evaluation_date) },
        { label: "İşlem", render: r => <Btn size="sm" variant="danger" onClick={() => del(r.id)}><Trash2 size={10}/></Btn> },
      ]}/>

      <Modal open={!!modal} onClose={() => setModal(null)} title="Yeni Tedarikçi Değerlendirmesi" size="lg">
        {modal && <div className="grid grid-cols-2 gap-3">
          <Select label="Tedarikçi *" value={modal.supplier_id} onChange={e => setModal({ ...modal, supplier_id: e.target.value })} data-testid="se-supplier"><option value="">Seçiniz...</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
          <Input label="Sonraki Değerlendirme" type="date" value={modal.next_evaluation_date || ""} onChange={e => setModal({ ...modal, next_evaluation_date: e.target.value })}/>
          {criteria.map(c => (
            <div key={c.key} className="col-span-2 bg-slate-50 border border-slate-200 rounded p-2">
              <div className="text-xs font-semibold mb-1">{c.label}</div>
              <div className="flex gap-1">{[1,2,3,4,5].map(n => <button key={n} onClick={() => setModal({ ...modal, scores: { ...modal.scores, [c.key]: n } })} className={`px-3 py-1 text-xs rounded border ${modal.scores[c.key] === n ? "bg-indigo-600 text-white border-indigo-700" : "bg-white border-slate-200 hover:bg-slate-100"}`} data-testid={`score-${c.key}-${n}`}>{n}</button>)}</div>
            </div>
          ))}
          <div className="col-span-2"><Textarea label="Not / Sonuç" value={modal.result_notes || ""} onChange={e => setModal({ ...modal, result_notes: e.target.value })}/></div>
        </div>}
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-supplier-eval">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}

/* ============= RETAINED SAMPLES TAB ============= */
export function RetainedSamplesTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [pos, setPos] = useState([]);
  const [modal, setModal] = useState(null);
  const dr = useScopedDateRange();
  const [filters, setFilters] = useState({ product: "", project: "", customer: "", lot: "", storage_location: "", state: "", expiry_status: "", search: "" });
  const load = useCallback(() => {
    const { start, end } = dr.resolveRange();
    const q = { ...filters, start, end };
    Object.keys(q).forEach(k => { if (q[k] === "" || q[k] === undefined) delete q[k]; });
    return Promise.all([api.get("/gmp/retained-samples", q), api.get("/production-orders", { status: "completed" }).catch(() => [])]).then(([r, p]) => { setRows(r); setPos(p); });
  }, [filters, dr]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);
  const genPdf = () => {
    const { start, end } = dr.resolveRange();
    downloadFilteredPdf("/gmp/retained-samples/pdf", { ...filters, start, end });
  };
  const resetAll = () => { dr.reset(); setFilters({ product: "", project: "", customer: "", lot: "", storage_location: "", state: "", expiry_status: "", search: "" }); };
  const empty = { production_order_id: "", production_order_no: "", customer_name: "", brand: "", product_name: "", batch_no: "", quantity: 2, unit: "adet", storage_location: "Şahit Numune Dolabı", taken_at: today(), retention_end: "", notes: "" };
  const save = async () => {
    try {
      if (modal.production_order_id) {
        // PO-based (existing endpoint, admin only)
        await api.post("/gmp/retained-samples", {
          production_order_id: modal.production_order_id,
          quantity: modal.quantity, unit: modal.unit,
          storage_location: modal.storage_location,
          taken_at: modal.taken_at, retention_end: modal.retention_end,
          notes: modal.notes,
        });
      } else {
        // Manual (no PO) — iter322
        if (!(modal.product_name || "").trim()) { alert("Üretim emri yoksa ürün adı zorunludur"); return; }
        await api.post("/gmp/retained-samples/manual", modal);
      }
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const dispose = async (id) => { const reason = window.prompt("İmha gerekçesi:"); if (reason === null) return; await api.post(`/gmp/retained-samples/${id}/dispose`, { reason }); load(); };
  const stateBadge = (r) => { if (r.state === "disposed") return <Badge level="inactive">İmha Edildi</Badge>; const l = dueLevel(r.retention_end); return l === "danger" ? <Badge level="missing">🔴 Süre Doldu</Badge> : l === "warn" ? <Badge level="attention">🟡 Yaklaşıyor</Badge> : <Badge level="ok">🟢 Saklamada</Badge>; };
  return (
    <div>
      <GMPFilterBar
        {...dr}
        testidPrefix="ret"
        onReset={resetAll}
        onPdf={genPdf}
        extraFilters={<>
          <Input label="Ürün" value={filters.product} onChange={e => setFilters({ ...filters, product: e.target.value })} placeholder="kısmi arama" data-testid="ret-product"/>
          <Input label="Proje No" value={filters.project} onChange={e => setFilters({ ...filters, project: e.target.value })} data-testid="ret-project"/>
          <Input label="Müşteri" value={filters.customer} onChange={e => setFilters({ ...filters, customer: e.target.value })} data-testid="ret-customer"/>
          <Input label="Üretim / Lot" value={filters.lot} onChange={e => setFilters({ ...filters, lot: e.target.value })} data-testid="ret-lot"/>
          <Input label="Saklama Yeri" value={filters.storage_location} onChange={e => setFilters({ ...filters, storage_location: e.target.value })} data-testid="ret-storage"/>
          <Select label="Durum" value={filters.state} onChange={e => setFilters({ ...filters, state: e.target.value })} data-testid="ret-state"><option value="">Tümü</option><option value="stored">Saklamada</option><option value="disposed">İmha Edildi</option></Select>
          <Select label="Süre Durumu" value={filters.expiry_status} onChange={e => setFilters({ ...filters, expiry_status: e.target.value })} data-testid="ret-expiry-status"><option value="">Tümü</option><option value="nearing">Süresi Yaklaşan</option><option value="expired">Süresi Dolan</option></Select>
        </>}
      />
      <div className="flex justify-end mb-3"><Btn onClick={() => setModal({ ...empty })} data-testid="add-retained"><Plus size={12}/> Şahit Numune Ekle</Btn></div>
      <Table testid="retained-table" rows={rows} columns={[
        { label: "Üretim", render: r => <span className="font-mono text-xs">{r.production_no}</span> },
        { label: "Proje", render: r => r.project_no || "—" },
        { label: "Müşteri", render: r => r.customer_name },
        { label: "Ürün", render: r => r.product_name },
        { label: "Batch/Lot", render: r => r.batch_no || "—" },
        { label: "Miktar", render: r => `${r.quantity} ${r.unit}` },
        { label: "Ayrılma", render: r => r.taken_at },
        { label: "Saklama Bitiş", render: r => dueBadge(r.retention_end) },
        { label: "Yer", render: r => r.storage_location },
        { label: "Durum", render: r => stateBadge(r) },
        { label: "İşlem", render: r => r.state === "stored" ? <Btn size="sm" variant="danger" onClick={() => dispose(r.id)} data-testid={`dispose-${r.id}`}>İmha</Btn> : null },
      ]}/>

      <Modal open={!!modal} onClose={() => setModal(null)} title="Yeni Şahit Numune (Manuel)" size="lg">
        {modal && <div className="grid grid-cols-2 gap-3">
          <Select label="Üretim Emri (opsiyonel)" value={modal.production_order_id} onChange={e => setModal({ ...modal, production_order_id: e.target.value })} data-testid="rs-po"><option value="">— Yok (manuel) —</option>{pos.map(p => <option key={p.id} value={p.id}>{p.prod_no} — {p.product_name}</option>)}</Select>
          {!modal.production_order_id && (
            <>
              <Input label="Müşteri Adı" value={modal.customer_name} onChange={e => setModal({ ...modal, customer_name: e.target.value })} data-testid="rs-customer" placeholder="Elle yazılabilir"/>
              <Input label="Marka" value={modal.brand} onChange={e => setModal({ ...modal, brand: e.target.value })} data-testid="rs-brand"/>
              <Input label="Ürün Adı *" value={modal.product_name} onChange={e => setModal({ ...modal, product_name: e.target.value })} data-testid="rs-product"/>
              <Input label="Lot No" value={modal.batch_no} onChange={e => setModal({ ...modal, batch_no: e.target.value })} data-testid="rs-lot"/>
            </>
          )}
          <Input label="Miktar" type="number" value={modal.quantity} onChange={e => setModal({ ...modal, quantity: parseFloat(e.target.value) })}/>
          <Select label="Birim" value={modal.unit} onChange={e => setModal({ ...modal, unit: e.target.value })}><option value="adet">adet</option><option value="ml">ml</option><option value="g">g</option></Select>
          <Input label="Saklama Yeri" value={modal.storage_location} onChange={e => setModal({ ...modal, storage_location: e.target.value })}/>
          <Input label="Ayrılma Tarihi" type="date" value={modal.taken_at} onChange={e => setModal({ ...modal, taken_at: e.target.value })}/>
          <Input label="Saklama Bitiş (boş=12 ay sonra)" type="date" value={modal.retention_end || ""} onChange={e => setModal({ ...modal, retention_end: e.target.value })}/>
          <div className="col-span-2"><Textarea label="Not" value={modal.notes || ""} onChange={e => setModal({ ...modal, notes: e.target.value })}/></div>
        </div>}
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-retained">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}
